import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, GitCompareArrows, Loader2 } from 'lucide-react';
import { CrossDayAnalogueAnalysisReport } from '@/components/CrossDayAnalogueAnalysisReport';
import { crossDayAnalogueService, type CrossDayAnalogueNarrative } from '@/services/api';

function formatReferenceDateLabel(value: string): string {
  try {
    const date = new Date(`${value}T12:00:00`);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

export function CrossDayAnalogueAnalysisPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [narrative, setNarrative] = useState<CrossDayAnalogueNarrative | null>(null);
  const [referenceDate, setReferenceDate] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const response = await crossDayAnalogueService.getLatest();
      if (cancelled) return;
      if (!response?.narrative) {
        setError('No cross-day analogue narrative is available yet.');
        setNarrative(null);
      } else {
        setNarrative(response.narrative);
        setReferenceDate(response.reference_date);
      }
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const windowLabel =
    narrative?.metadata.window_start && narrative?.metadata.window_end
      ? `${narrative.metadata.window_start} → ${narrative.metadata.window_end}`
      : null;

  return (
    <div className="cross-day-analogue-analysis-page flex min-h-screen flex-col bg-gray-50 print:bg-white">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="cross-day-analogue-analysis-page__header shrink-0 border-b border-gray-200 bg-white print:hidden"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center gap-4 px-6 py-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-900">
              <GitCompareArrows className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="tracking-tight text-gray-900">Cross Day Analogue Analysis</h2>
              <p className="truncate text-sm text-gray-500">
                {referenceDate
                  ? `${formatReferenceDateLabel(referenceDate)}${windowLabel ? ` · ${windowLabel}` : ''}`
                  : 'Loading…'}
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      <main className="cross-day-analogue-analysis-page__main mx-auto w-full max-w-7xl flex-1 px-6 py-8 md:py-10 print:max-w-none print:p-0">
        <div className="cross-day-analogue-analysis-page__shell overflow-hidden rounded-3xl border border-gray-200 bg-white print:overflow-visible print:rounded-none print:border-0 print:shadow-none">
          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center gap-3 p-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="text-gray-500">Loading cross-day narrative…</span>
            </div>
          ) : error ? (
            <div className="p-12 text-center text-gray-500">{error}</div>
          ) : narrative ? (
            <CrossDayAnalogueAnalysisReport narrative={narrative} />
          ) : null}
        </div>
      </main>
    </div>
  );
}
